import { QueryClient } from '@tanstack/react-query'

/**
 * Shared TanStack Query client for the app.
 *
 * Replaces the ad-hoc caching/loading-state patterns that the legacy
 * AngularJS services built manually around `$http` (e.g. `$q` promise
 * caching in `authService.js`, custom `isLoading` flags on scope). Server
 * state (customers, coupons, banners, feature toggles, etc.) should be
 * fetched/mutated through hooks built on top of this client rather than by
 * calling the HTTP client directly from components.
 *
 * Defaults chosen for this app:
 * - `staleTime: 30s` — most screens are admin/back-office CRUD tables that
 *   don't need to refetch on every focus/mount; 30s avoids redundant
 *   requests while still feeling "live enough" without manual invalidation.
 * - `retry: 1` — the backend is an internal Express API (not a flaky public
 *   one), so a single retry is enough to smooth over transient network
 *   blips without hammering the server or hanging the UI on real failures.
 * - `refetchOnWindowFocus: false` — this is an internal admin tool typically
 *   left open in a background tab; refetching every time the tab regains
 *   focus is more disruptive (resets scroll/selection) than useful here.
 * - Mutations default to `retry: 0` since retrying non-idempotent writes
 *   (create/update/delete) automatically is unsafe unless a specific
 *   mutation opts back in.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30 * 1000,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
})

export default queryClient
